import React, { useEffect, useMemo, useRef, useState } from "react";
import { message, Dropdown, Modal } from "antd";
import {
  MenuOutlined,
  AuditOutlined,
  SwapOutlined,
  LoadingOutlined,
  BarChartOutlined,
  ImportOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import EnhancedTable from "@/hocComponents/EnhancedTable";
import http from "@/utils/http";
import api from "@/utils/api";
import { bankRecDate as convertArrayToDayjs } from "@/views/BankReconciliation/BankRec/dates";
import WireTransferModal from "../WireTransferModal";
import { buildRequestParams, formatDate } from "./utils";

const TRANS_TYPE_OPTIONS = [
  { value: "CP", label: "Bank - Debit" },
  { value: "CR", label: "Bank - Credit" },
];
const TRANS_TYPE_LABEL_MAP = { CP: "Bank - Debit", CR: "Bank - Credit" };

// 仅当 BAI Code 为以下几种时，才展示 "Record Fund Transfer"
const RECORD_FUND_TRANSFER_BAI_CODES = ["501", "644", "399", "201"];
const canShowRecordFundTransfer = (record) =>
  RECORD_FUND_TRANSFER_BAI_CODES.includes(String(record?.baiCode ?? ""));

const iconStyle = { color: "#00c0ef", fontSize: 17 };
const PAGE_SIZE_OPTIONS = ["1000", "500", "200", "100"];

const saveReconcilePageState = (storageKey, payload) => {
  sessionStorage.setItem(storageKey, JSON.stringify(payload));
};
const getAuthToken = () => localStorage.getItem("tokenPC");

export default function MergeTab({ year, refreshKey, onLoadingChange }) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 1000, total: 0 });
  const [entityList, setEntityList] = useState([]);
  const [bankNameList, setBankNameList] = useState([]);
  const [baiCodeList, setBaiCodeList] = useState([]);
  const [wireTransferVisible, setWireTransferVisible] = useState(false);
  const [wireTransferRecord, setWireTransferRecord] = useState(null);
  const [jumpLoadingId, setJumpLoadingId] = useState(null);
  const jumpLoadingRef = useRef(null);
  const requestIdRef = useRef(0);

  // ---------------- 数据加载 ----------------
  const load = async (page, count, nextFilters) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = buildRequestParams(
        { page, count: count || 1000, year },
        nextFilters,
        ["date"],
      );
      const res = await http.post(api.getShowUnreconciled, params);
      if (requestId !== requestIdRef.current) return;
      if (res?.code === "1") {
        setList(res.data?.list || []);
        setPagination({
          current: res.data?.pageNum || page,
          pageSize: res.data?.pageSize || count,
          total: res.data?.total || 0,
        });
      } else {
        message.error(res?.msg);
      }
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      message.error("Failed to load data, please try again later");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  const loadOptions = async (apiUrl, setter) => {
    try {
      const res = await http.post(apiUrl, { year });
      if (res?.code === "1") {
        setter((res.data || []).map((i) => ({ value: i.value, label: i.label })));
      }
    } catch (e) {
      setter([]);
    }
  };

  useEffect(() => {
    setFilters({});
    load(1, pagination.pageSize, {});
  }, [year, refreshKey]);

  useEffect(() => {
    loadOptions(api.getBankRecByTrust, setEntityList);
    loadOptions(api.getBankRecByBankName, setBankNameList);
    loadOptions(api.getBaiCodeDict, setBaiCodeList);
  }, [year]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading]);

  // ---------------- 行操作 ----------------
  const handleJumpBank = async (record) => {
    if (jumpLoadingRef.current) {
      message.warning("Processing, please wait...");
      return;
    }
    const bankId = record?.bankId;
    const reconcileDateSt = convertArrayToDayjs(record?.date)?.format("YYYY-MM-DD");
    if (!bankId || !reconcileDateSt) {
      message.error("Missing bankId or date, cannot open reconcile page");
      return;
    }

    jumpLoadingRef.current = record.bankTransNewId;
    setJumpLoadingId(record.bankTransNewId);
    try {
      const res = await http.get(api.getBankByLock, { params: { bankId } });
      if (res?.code !== "1") {
        Modal.error({ title: "Notice", content: res?.msg, centered: true, okText: "OK" });
        return;
      }
      const storageKey = `ut_page_${Date.now()}`;
      saveReconcilePageState(storageKey, {
        record,
        reconcileDateSt,
        token: getAuthToken(),
      });
      window.open(`/#/BankReconciliation/BankRec/UTPage?key=${storageKey}`, "_blank");
    } catch (err) {
      message.error("Failed to check bank lock status");
    } finally {
      jumpLoadingRef.current = null;
      setJumpLoadingId(null);
    }
  };

  const handleWireTransfer = (record) => {
    setWireTransferRecord(record);
    setWireTransferVisible(true);
  };

  // Fund Transfer / Credit / Debit 只有页面名不同，合并成一个函数
  const openRecordPage = (pageName, record) => {
    const params = new URLSearchParams({
      bankName: record?.bankName || "",
      baiCode: record?.baiCode || "",
      year: year || dayjs().year(),
    });
    window.open(
      `/#/BankReconciliation/BankRec/component/${pageName}?${params.toString()}`,
      "_blank",
    );
  };

  const menuActionHandlers = {
    jumpBank: handleJumpBank,
    wireTransfer: handleWireTransfer,
    RecordFundTransfer: (r) => openRecordPage("FundTransferPage", r),
    recordCredit: (r) => openRecordPage("RecordCreditPage", r),
    recordDebit: (r) => openRecordPage("RecordDebitPage", r),
  };
  const handleMenuClick = (e, record) => menuActionHandlers[e.key]?.(record);

  // ---------------- 列 ----------------
  const columns = useMemo(
    () => [
      {
        title: "Action",
        dataIndex: "index",
        align: "center",
        width: 60,
        render: (text, record) => {
          const isJumpLoading = jumpLoadingId === record.bankTransNewId;
          const menuItems = [
            {
              key: "jumpBank",
              icon: isJumpLoading ? (
                <LoadingOutlined style={iconStyle} spin />
              ) : (
                <AuditOutlined style={iconStyle} />
              ),
              label: <div>Reconcile</div>,
              disabled: isJumpLoading,
            },
            canShowRecordFundTransfer(record) && {
              key: "RecordFundTransfer",
              icon: <BarChartOutlined style={iconStyle} />,
              label: <div>Record Fund Transfer</div>,
            },
            record?.showWireTransferAction && {
              key: "wireTransfer",
              icon: <SwapOutlined style={iconStyle} />,
              label: <div>Wire Transfer</div>,
            },
            {
              key: "recordCredit",
              icon: <ImportOutlined style={iconStyle} />,
              label: <div>Record Credit</div>,
            },
            {
              key: "recordDebit",
              icon: <ExportOutlined style={iconStyle} />,
              label: <div>Record Debit</div>,
            },
          ].filter(Boolean);

          return (
            <Dropdown
              menu={{ items: menuItems, onClick: (e) => handleMenuClick(e, record) }}
              trigger={["click"]}
            >
              <MenuOutlined style={{ cursor: "pointer", fontSize: 16, color: "#0e78a9" }} />
            </Dropdown>
          );
        },
      },
      {
        title: "Trans Type",
        dataIndex: "type",
        filterKey: "type",
        filterType: "select",
        selectOptions: TRANS_TYPE_OPTIONS,
        render: (text) => TRANS_TYPE_LABEL_MAP[text] || text,
      },
      { title: "Amount", dataIndex: "amount", filterKey: "amount", isBracketAmountFlag: true },
      { title: "Check #", dataIndex: "checkNo", filterKey: "checkNo" },
      { title: "Date", dataIndex: "date", filterKey: "date", filterType: "date", render: formatDate },
      {
        title: "Description",
        dataIndex: "remark",
        filterKey: "remark",
        width: 220,
        render: (text, record) => {
          if (record?.remark && record?.trustNameRemark) {
            return (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{record.remark}</span>
                <span>({record.trustNameRemark})</span>
              </div>
            );
          }
          return record?.remark || record?.trustNameRemark || "";
        },
      },
      {
        title: "BAI Code",
        dataIndex: "baiDesc",
        filterKey: "baiCode",
        filterType: "select",
        selectOptions: baiCodeList,
        width: 150,
      },
      {
        title: "Bank Name",
        dataIndex: "bankName",
        filterKey: "bankName",
        filterType: "select",
        selectOptions: bankNameList,
      },
      { title: "Account #", dataIndex: "bankAccount", filterKey: "bankAccount" },
      { title: "PPM Bank #", dataIndex: "bank" },
      { title: "Account Remark", dataIndex: "bankRemark" },
      {
        title: "Entity",
        dataIndex: "trust",
        filterKey: "trust",
        width: 180,
        filterType: "select",
        selectOptions: entityList,
        render: (text, record) => {
          if (record?.trustName && record?.trust) {
            return (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{record.trustName}</span>
                <span>({record.trust})</span>
              </div>
            );
          }
          return record?.trust || record?.trustName || "";
        },
      },
    ],
    [bankNameList, entityList, baiCodeList, jumpLoadingId, year],
  );

  const handleTableChange = (p, tableFilters) => {
    const merged = { ...filters };
    Object.keys(tableFilters || {}).forEach((k) => {
      if (tableFilters[k]?.length > 0) merged[k] = tableFilters[k][0];
    });
    load(p.current || 1, p.pageSize || pagination.pageSize, merged);
  };

  return (
    <>
      <EnhancedTable
        columns={columns}
        dataSource={list}
        loading={loading}
        rowKey={(r) => r?.bankTransNewId ?? r?.id}
        filters={filters}
        onFilterChange={(k, v) => setFilters((prev) => ({ ...prev, [k]: v }))}
        onFilterSubmit={(f) => {
          setFilters(f);
          load(1, pagination.pageSize, f);
        }}
        onChange={handleTableChange}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          showTotal: (t) => `Total ${t} items`,
        }}
        scroll={{ y: "calc(100vh - 320px)" }}
      />

      <WireTransferModal
        visible={wireTransferVisible}
        record={wireTransferRecord}
        onClose={() => {
          setWireTransferVisible(false);
          setWireTransferRecord(null);
        }}
        onSuccess={() => load(pagination.current, pagination.pageSize, filters)}
      />
    </>
  );
}